import { isServiceWeekLabel, type ServiceWeekReviewHeader } from "./service-week-approval";

/** Parsed presentation truth only. Private keys and transport revisions belong to controllers. */
export interface ClientArtifactServiceWeekHeader extends ServiceWeekReviewHeader {
  preparedByLabel: string;
  preparedByRole: "admin" | "supervisor";
  generatedAt: string;
  dataThroughAt: string;
}

export const CLIENT_ARTIFACT_INTEGRITY = "Every ✓ was recorded by a device at the time shown. Times without a ✓ are marked for what they are.";

/** Only shipped client-artifact sentences. R-07 belongs to scans and R-08 to SWAF review. */
export const clientArtifactRefusalCopy = {
  "R-01": (artifact: string) => `This week has not been approved. Approve it in Schedule, Timesheets before exporting ${artifact}.`,
  "R-02": (artifact: string) => `There are no scheduled shifts for this site and week, so there is no ${artifact} to export.`,
  "R-03": "This week is too large to export here. Contact SecOps support for help with this week.",
  "R-04": (artifact: string) => `No active client sites are available for ${artifact}.`,
  "R-05": "There are too many client sites to list here. Ask SecOps support for the complete list.",
  "R-06": (artifact: string) => `${artifact} is unavailable right now. Check your access or try again.`,
  "R-09": (artifact: string) => `This week's service facts are incomplete, so ${artifact} cannot be exported yet. Open it in Schedule, Timesheets to see what is missing.`,
} as const;

/** Validate exactly the host-normalized truth; never repair or truncate printable labels. */
export function isClientArtifactPrintableLabel(value: unknown, max = 120): value is string {
  if (!isServiceWeekLabel(value)) return false;
  // Compatibility characters can visually spell contact details or private references.
  // Use their projection only for refusal; retain/count the original NFC display truth.
  const shape = value.normalize("NFKC");
  return [...value].length <= max
    && !/[\p{Cc}\p{Cf}\p{Cs}\p{Default_Ignorable_Code_Point}]/u.test(value)
    && !/@|https?:|www\.|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32,}|(?:eyJ|sb_secret_|sk_live_)/i.test(shape)
    && !/(?:^|[^A-Za-z0-9_])chk_[A-Za-z0-9]{12,}(?:[^A-Za-z0-9_]|$)|service_role|bearer\s+[A-Za-z0-9._-]{8,}|Expo(?:nent)?PushToken\[/i.test(shape)
    && !/^\+?[\d ()-]{7,}$/.test(shape) && !/^-?\d{1,3}\.\d+\s*,\s*-?\d{1,3}\.\d+$/.test(shape)
    && !/[a-z][a-z0-9+.-]*:\/\/|(?:mailto|tel|data|javascript):|(?:^|[^a-z0-9_])(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:$|[^a-z0-9_])/i.test(shape)
    && !/(?:^|[^\p{L}\p{N}])(?:user|usr|auth|account|acct|client|site|assignment|assign|slot|event|device|officer|guard|membership|member|ref|id)[_:/#-][\p{L}\p{N}_-]{4,}(?:$|[^\p{L}\p{N}_])/iu.test(shape)
    && !/(?:^|[^A-Za-z0-9_-])(?=[A-Za-z0-9_-]{32,}(?:[^A-Za-z0-9_-]|$))(?=[A-Za-z0-9_-]*[0-9])[A-Za-z0-9_-]*[A-Za-z][A-Za-z0-9_-]*/.test(shape);
}
export function isClientArtifactPreparedBy(value: unknown, role: unknown): boolean {
  if (role !== "admin" && role !== "supervisor") return false;
  const roleLabel = `Account ${role}`;
  if (value === roleLabel) return true;
  if (typeof value !== "string" || !value.endsWith(` · ${roleLabel}`)) return false;
  return isClientArtifactPrintableLabel(value.slice(0, -(` · ${roleLabel}`.length)), 80);
}
